import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { lookupEncounterMrns } from "@/api/encounterMrnLookup";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { parseEncounterIds } from "./parseEncounterIds";

const MAX_ENCOUNTER_IDS = 1000;

export default function EncounterMrnLookup() {
  const [rawInput, setRawInput] = useState("");

  const lookupMutation = useMutation({
    mutationFn: (encounterIds: string[]) => lookupEncounterMrns(encounterIds),
    onSuccess: (data) => {
      toast.success(`Looked up ${data.length} encounter id${data.length === 1 ? "" : "s"}`);
    },
    onError: (error) => {
      toast.error(`Lookup failed: ${error.message}`);
    },
  });

  const results = lookupMutation.data ?? [];

  const handleLookup = () => {
    const encounterIds = parseEncounterIds(rawInput);
    if (encounterIds.length === 0) {
      toast.error("Paste at least one encounter id");
      return;
    }
    if (encounterIds.length > MAX_ENCOUNTER_IDS) {
      toast.error(`Too many encounter ids (max ${MAX_ENCOUNTER_IDS} per lookup)`);
      return;
    }
    lookupMutation.mutate(encounterIds);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Encounter MRN Lookup</h1>
        <p className="text-sm text-muted-foreground">
          Paste encounter ids (comma, space, or semicolon separated) to resolve their MRNs.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="encounter-ids">Encounter ids</Label>
        <Textarea
          id="encounter-ids"
          placeholder="e.g. 11111111-1111-1111-1111-111111111111, 22222222-2222-2222-2222-222222222222"
          value={rawInput}
          onChange={(e) => setRawInput(e.target.value)}
        />
      </div>

      <Button onClick={handleLookup} disabled={lookupMutation.isPending}>
        {lookupMutation.isPending ? "Looking up…" : "Look up MRNs"}
      </Button>

      {results.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Encounter ID</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((result) => (
              <TableRow key={result.encounterId}>
                <TableCell className="font-mono text-xs">{result.encounterId}</TableCell>
                <TableCell>{result.providerId ?? "—"}</TableCell>
                <TableCell>{result.mrn ?? "—"}</TableCell>
                <TableCell>{result.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
